import { Link } from "react-router-dom";
import { ArrowRight, Home, Gift, Heart, ScrollText, Clock, Boxes } from "lucide-react";
import { CommissionFormInline } from "@/components/CommissionForm";

const audiences = [
  {
    id: "real-estate",
    icon: Home,
    eyebrow: "Real estate",
    title: "Settlement gifts your clients will keep",
    body:
      "A handcrafted board signed off with the new owners' family name turns a transaction into a memory. Far more memorable than a bottle of wine — and stays on their bench for decades.",
    suggestion: "Engrave the family name and settlement date.",
  },
  {
    id: "corporate",
    icon: Gift,
    eyebrow: "Corporate · Christmas",
    title: "Staff and client gifts that don't end up in a drawer",
    body:
      "Skip the generic hamper. A timber piece engraved with your company mark lands differently — useful, beautiful, and unmistakably premium. Ideal for end-of-year client thank-yous or long-service awards.",
    suggestion: "Engrave a company logo or a personal message per recipient.",
  },
  {
    id: "weddings",
    icon: Heart,
    eyebrow: "Weddings",
    title: "Gifts the couple will use for a lifetime",
    body:
      "A board engraved with the couple's names and wedding date becomes part of their kitchen story. Equally lovely for hens / stags' parties, bridal-party thank-yous, or the favourite-uncle wedding-gift slot.",
    suggestion: "Engrave names, date, or a short line from a vow.",
  },
];

const details = [
  {
    icon: ScrollText,
    title: "Engraving included",
    body:
      "Family name, company logo, dates, short messages — engraved by hand into the timber. Bring your logo as a clean image and I'll handle the rest.",
  },
  {
    icon: Boxes,
    title: "Minimum order: 10 boards",
    body:
      "Bulk gifting starts at ten pieces. Volume pricing available on request — get in touch with the brief and I'll send a personal quote.",
  },
  {
    icon: Clock,
    title: "Two-week lead time",
    body:
      "Two weeks for an order of ten boards. Larger runs take a little longer. Get your Christmas brief in by late October to be safe.",
  },
];

export default function GiftsPage() {
  return (
    <div data-testid="gifts-page" className="pb-24">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <div className="eyebrow mb-5">Gifting</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
            Memorable gifts, <span className="italic">made by hand</span>.
          </h1>
        </div>
        <div className="md:col-span-5 text-[#2C2421]/80 leading-relaxed text-lg">
          For real estate settlements, corporate Christmas, weddings, and any
          occasion that deserves more than the usual. Each piece is engraved by
          hand and signed off in the workshop.
        </div>
      </section>

      {/* Hero image */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16">
        <div className="aspect-[16/9] bg-[#F5F2EB] overflow-hidden">
          <img
            src="/lifestyle/in-use.jpg"
            alt="Engraved handmade serving board"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Three audiences */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 md:mt-32">
        <div className="eyebrow mb-4">Who it's for</div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">
          Designed for the people who give properly.
        </h2>

        <div className="mt-14 grid md:grid-cols-3 gap-10 md:gap-12">
          {audiences.map((a) => (
            <div key={a.id} data-testid={`gift-audience-${a.id}`} className="">
              <a.icon size={26} className="text-[#6F4E37] mb-6" />
              <div className="eyebrow mb-3">{a.eyebrow}</div>
              <h3 className="font-serif text-2xl md:text-[28px] leading-tight mb-5">
                {a.title}
              </h3>
              <p className="text-[#2C2421]/80 leading-relaxed">{a.body}</p>
              <p className="mt-6 text-sm text-[#6F4E37] italic border-l border-[#A89F91]/40 pl-4">
                {a.suggestion}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Engraving + practicals strip */}
      <section className="bg-[#F5F2EB] mt-24 md:mt-32 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-4">
              <div className="eyebrow mb-4">The detail</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Engraved, packed, and ready to give.
              </h2>
              <p className="mt-6 text-[#2C2421]/80 leading-relaxed">
                Every gift order leaves the workshop sanded to a glassy finish,
                signed, oiled, and packaged with a care card so the recipient
                knows how to look after it.
              </p>
            </div>

            <div className="md:col-span-8 grid sm:grid-cols-3 gap-8 md:gap-10">
              {details.map((d) => (
                <div key={d.title} data-testid={`gift-detail-${d.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <d.icon size={22} className="text-[#6F4E37] mb-4" />
                  <h3 className="font-serif text-xl mb-3">{d.title}</h3>
                  <p className="text-[#2C2421]/80 leading-relaxed text-[15px]">
                    {d.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="max-w-[920px] mx-auto px-6 md:px-12 mt-24 md:mt-32">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4">Start your gift order</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
            Tell me what you have in mind.
          </h2>
          <p className="mt-6 text-[#2C2421]/75 leading-relaxed max-w-xl mx-auto">
            Send through the rough numbers, what you'd like engraved, and when
            you need them by. I'll come back personally with a quote within
            48 hours.
          </p>
        </div>

        <CommissionFormInline
          preset={{
            project_type: "Wedding or gift piece",
            details:
              "Gift order — please share:\n• Approx number of pieces (min. 10)\n• Occasion (settlement / corporate / wedding / other)\n• What you'd like engraved (logo, family name, date, message)\n• When you need them by\n• Any reference image links\n",
          }}
        />
      </section>

      <div className="text-center mt-20">
        <Link
          to="/shop"
          data-testid="gifts-shop-link"
          className="inline-flex items-center gap-3 border border-[#2C2421] text-[#2C2421] px-7 py-4 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
        >
          Browse the collection <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
